import { SaveButton, Toolbar, ToolbarProps, useRecordContext, useSaveContext } from 'react-admin';
import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import FileContentInput from './FileContentInput';
import { useFormContext } from 'react-hook-form';
import { format, addDays } from 'date-fns';
import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');

export const ConfirmSaveToolbar = (
  props: ToolbarProps & { disabled?: boolean; agreementChangeWarningText: string; dialogTitle?: string }
) => {
  const { saving, save } = useSaveContext();
  const { setValue, handleSubmit } = useFormContext();
  const [openDialog, setOpenDialog] = useState(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const [isChecked, setIsChecked] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs(addDays(new Date(), 30)));

  const onSubmitWithUpdatedDeadline = async (currentFormValues: Record<string, any>) => {
    // ダイアログ保存時に本来の保存処理を呼び出す
    if (save) {
      try {
        await save(currentFormValues);
      } catch (error) {
        throw error;
      } finally {
        setOpenDialog(false);
      }
    } else {
      setOpenDialog(false);
    }
  };

  const handleDialogConfirm = () => {
    // 保存する情報に追加
    setValue('agreement', isChecked);
    setValue('agreementDeadlineAt', selectedDate?.toDate(), {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });

    // 2. handleSubmit を使って、更新されたフォーム値で onSubmitWithUpdatedDeadline を実行
    // setValue の直後でも、handleSubmit は最新のフォーム状態を取得してコールバックに渡す
    handleSubmit(onSubmitWithUpdatedDeadline)();
  };

  const handleClickOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDateChange = (newDate: Dayjs | null) => {
    setSelectedDate(newDate);
  };

  return (
    <>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <SaveButton onClick={handleClickOpen} disabled={saving || props.disabled}></SaveButton>
      </Toolbar>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle id="confirm-save-dialog-title">{props.dialogTitle || '変更確認'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ marginBottom: '1em', whiteSpace: 'pre-wrap' }}>
            {props.agreementChangeWarningText}
          </DialogContentText>

          <Stack direction="column" alignItems="left" sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox checked={isChecked} onChange={handleCheckboxChange} name="myCheckbox" color="primary" />
              }
              label="同意期限を変更する"
            />

            <DatePicker
              label="日付を選択"
              value={selectedDate}
              onChange={handleDateChange}
              disabled={!isChecked}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal'
                }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            キャンセル
          </Button>
          <Button onClick={handleDialogConfirm} color="primary" autoFocus disabled={saving}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export interface ContractEditFormContentsProps {
  fileInputLabel: string;
  fileInputHelperText?: string;
}

export const ContractEditFormContents = ({
  fileInputLabel,
  fileInputHelperText = '上記ボタンからテキストファイル (.txt) を読み込むか、直接内容を入力/編集してください。'
}: ContractEditFormContentsProps) => {
  const record = useRecordContext();
  if (!record) {
    return null;
  }

  let formattedDisplayDeadlineDate = '未設定';
  const displayDeadlineValue = record['agreementDeadlineAt'];
  if (displayDeadlineValue) {
    try {
      formattedDisplayDeadlineDate = format(displayDeadlineValue, 'yyyy年MM月dd日');
    } catch (e) {}
  }

  return (
    <>
      <FileContentInput source={'contents'} label={fileInputLabel} helperText={fileInputHelperText} />

      <Typography variant="body2" color="textSecondary" sx={{ marginBottom: '1em', whiteSpace: 'pre-wrap' }}>
        現在の同意期限日: {formattedDisplayDeadlineDate}
        <br />
      </Typography>
    </>
  );
};
